import { useMemo } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useUserConfiguration } from "~/shared/hooks/use-user-configuration.ts";
import type {
	AddressBookItemWithFavorite,
	AddressBookQueryParams,
} from "~/shared/schemas/address-book-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { useAddressBookItems } from "./use-address-book.ts";

export const useAddressBookFilteredData = (
	customerId: number | undefined,
	selectedFilters: string[],
) => {
	const { employeeId } = useIsAuthenticated();
	const { isInternal } = useDeploymentType();

	const {
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		isCreating: isUserConfigCreating,
		isUpdating: isUserConfigUpdating,
		isDeleting: isUserConfigDeleting,
		userConfigs,
		isLoadingConfigs,
	} = useUserConfiguration();

	const queryParams: AddressBookQueryParams = useMemo(
		() => ({
			customerId: customerId ?? 0,
		}),
		[customerId],
	);

	const {
		data: addressBookItems,
		isLoading: isAddressBookItemsLoading,
		isFetching: isAddressBookItemsFetching,
		isError,
		error,
	} = useAddressBookItems(queryParams, true, isInternal);

	const favoriteIds = useMemo(() => {
		if (customerId === undefined) {
			return [];
		}
		return userConfigs
			.filter(
				(config) =>
					config.configType === ConfigurationType.AddressFavorite &&
					config.customerId === customerId,
			)
			.map((config) => Number(config.configValue));
	}, [userConfigs, customerId]);

	const pickupDefaultId = useMemo(() => {
		if (customerId === undefined) {
			return undefined;
		}
		return userConfigs.find(
			(config) =>
				config.configType === ConfigurationType.PickDefaultAddress &&
				config.customerId === customerId,
		)?.configValue;
	}, [userConfigs, customerId]);

	const deliveryDefaultId = useMemo(() => {
		if (customerId === undefined) {
			return undefined;
		}
		return userConfigs.find(
			(config) =>
				config.configType === ConfigurationType.DropDefaultAddress &&
				config.customerId === customerId,
		)?.configValue;
	}, [userConfigs, customerId]);

	const data = useMemo((): AddressBookItemWithFavorite[] => {
		const items = addressBookItems ?? [];

		// Get IDs of legacy addresses that have been converted to new addresses
		const convertedLegacyIds = new Set(
			items
				.filter((item) => !item.isLegacy && item.legacyAddressId)
				.map((item) => item.legacyAddressId),
		);

		return items
			.filter(
				(item) =>
					!(item.isLegacy && convertedLegacyIds.has(item.addressBookItemId)),
			)
			.map((item) => {
				const address2Part = item.address2 ? `, ${item.address2}` : "";
				return {
					...item,
					isFavorite: favoriteIds.includes(item.addressBookItemId),
					isPickupDefault:
						pickupDefaultId === item.addressBookItemId.toString(),
					isDeliveryDefault:
						deliveryDefaultId === item.addressBookItemId.toString(),
					fullAddress: `${item.address1}${address2Part}, ${item.city}, ${item.stateProvince} ${item.postalCode}`,
					pickupContactName:
						item.details?.find((detail) => detail.stopType === 1)
							?.contactName ?? "",
					deliveryContactName:
						item.details?.find((detail) => detail.stopType === 2)
							?.contactName ?? "",
				};
			})
			.sort((a, b) => {
				return (
					new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
				);
			});
	}, [addressBookItems, favoriteIds, pickupDefaultId, deliveryDefaultId]);

	const filteredData = useMemo(() => {
		if (selectedFilters.length === 0) return data;

		return data.filter((item) => {
			return selectedFilters.every((filter) => {
				switch (filter) {
					case "Verified": {
						return item.isValidated === true;
					}
					case "Not Legacy": {
						return item.isLegacy === false;
					}
					case "Favorites": {
						return item.isFavorite === true;
					}
					case "User Defaults": {
						return item.isPickupDefault || item.isDeliveryDefault;
					}
					default: {
						return true;
					}
				}
			});
		});
	}, [data, selectedFilters]);

	return {
		filteredData,
		data,
		isLoading: isAddressBookItemsLoading || isLoadingConfigs,
		isFetching: isAddressBookItemsFetching,
		isError,
		error,
		isUserConfigActionLoading:
			isUserConfigCreating || isUserConfigUpdating || isUserConfigDeleting,
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId: Number(employeeId),
		customerId: customerId ?? 0,
	};
};
